import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Dropdown } from 'antd';
import { Tooltip } from 'react-tooltip';

import RightScenes from '../components/Menu/BarDashboard/RightScenes/RightScenes';
import searchIcon from 'assets/images/search.svg'
import { useGetEntitiesListByOwner } from '../../../services/entity/querys';
import { Entity, Loan, Task, TaskState } from '../../../gql/graphql';
import { TOOLTIP_STATES } from '../../../@common/utils/constants/tooltip-states.constant';
import { RulesTasksCards } from '../Dashboard/RulesTaskCards.constanst';
import { useInfiniteScroll } from '../../../@common/utils/hooks/useInfiniteScroll';
import { ScrollSentinel } from '../../../components/Atoms/ScrollSentinel/ScrollSentinel';
import { useRedirectToDashboard } from '@common/utils/hooks/useRedirectToDashboard';

interface EntitiesProps {
  EntitiesViewContext: any;
  /** Cuando true (desde Onboarding/LeftBar), usa redirectToDashboard. Cuando false/undefined (desde Private), usa navigate */
  useRedirectToDashboard?: boolean;
}

const Entities = ({ EntitiesViewContext, useRedirectToDashboard: useRedirectToDashboardMode = false }: EntitiesProps) => {
  const navigate = useNavigate();
  const { redirectToDashboard } = useRedirectToDashboard();

  const [page, setPage] = useState(1);
  const [allLoadedEntities, setAllLoadedEntities] = useState<Entity[]>([]);
  
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  const [isPaused, setIsPaused] = useState(false);
  
  // Prevenir incremento de page durante cambio de filtros
  const isResettingRef = useRef(false);

  const ITEMS_PER_LOAD = 4;

  const { 
    data: entities,
    pagination,
    fetching,
    reexecute: getEntities 
  } = useGetEntitiesListByOwner({
    variables: { 
      search: searchTerm,
      page,
      limit: ITEMS_PER_LOAD,
    },
    pause: isPaused,
    requestPolicy: 'network-only'
  });

  const { visible, setHandlerVisible }: any = useContext(EntitiesViewContext);

  // Calcular si hay más elementos por cargar
  const hasMore = pagination?.page ? pagination.page < (pagination?.totalPages || 0) : false;

  // Acumular entities cuando llegan nuevas
  useEffect(() => {
    if (isPaused || !entities) return;

    if (entities.length > 0) {
      setAllLoadedEntities(prev => {
        if (page === 1) {
          return entities as Entity[];
        }
        const existingIds = new Set(prev.map(entity => entity._id));
        const newEntities = entities.filter(entity => !existingIds.has(entity._id));
        return [...prev, ...newEntities] as Entity[];
      });
    } else if (page === 1) {
      setAllLoadedEntities([]);
    }
  }, [entities, page, isPaused]);

  useEffect(() => {
    isResettingRef.current = true;
    setAllLoadedEntities([]);
    setPage(1);
    setIsPaused(true);

    const timer = setTimeout(() => {
      setIsPaused(false);
      isResettingRef.current = false;
    }, 100);

    return () => {
      clearTimeout(timer);
      isResettingRef.current = false;
    };
  }, [searchTerm]);

  const sentinelRef = useInfiniteScroll({
    hasMore,
    isLoading: fetching || isPaused,
    onLoadMore: () => {
      if (!isPaused && !isResettingRef.current) {
        setPage(prev => prev + 1);
      }
    }
  });

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  }

  const countGuarantors = (entity: Entity, isIncompletes?: boolean) => {
    return entity.ownerships?.filter((item: any) =>
      item.stakes[entity._id] && (!isIncompletes || item.state === TaskState.Approved)
    )
      .filter((item: any) => item.stakes[entity._id] >= 20)?.length;
  }

  const getGuarantorsName = (entity: Entity) =>
    entity.ownerships?.filter((item: any) => item.stakes[entity._id] >= 20)
      .map((item: any, index: number, array: any) => {
        if (index === array.length - 1 && array.length > 1)
          return `${array.length >= 3 ? ',' : ''}and ${item.name} ${item.lastname}`;

        return item.name + ' ' + item.lastname;
      })
      .filter((item: any) => !!item)
      .join(', ')
      .replace(/,([^,]*)$/, '$1');

  const countDocs = (entity: any, isIncompletes?: boolean) =>
    entity.docs?.filter((item: any) => (!isIncompletes || item.state === TaskState.Approved))?.length;

  const getStateLoan = (loan: any) => {
    return loan.state ?? 'active'
  }

  const filteredRulesTasksCards = RulesTasksCards.filter(item => !item.data?.hidden)
  const getTasksLast = (tasks: Task[]) => {
      for (const task of tasks) {
        if (!task.phase) {
          const rule = filteredRulesTasksCards.find(item => item.key === task.key);
          task.phase = rule?.phase[0];
        }
      }
  
      const groupedByPhase: any[] = tasks.reduce((acc: any, item: any) => {
        if (!acc[item.phase]) acc[item.phase] = [];
        acc[item.phase].push(item);
        return acc;
      }, {});
  
      let phaseLastKey, taskLast = [];
      for (const phaseKey of Object.keys(groupedByPhase)) {
        const isTaskPending = groupedByPhase[phaseKey as any].find((task: Task) => 
          task.state !== TaskState.Draft
        );
  
        const isFinishPhase = groupedByPhase[phaseKey as any].every((task: Task) => 
          task.state === TaskState.Approved
        );
        
        if (isTaskPending && !isFinishPhase) {
          phaseLastKey = phaseKey;
          taskLast = groupedByPhase[phaseKey as any];
          break;
        }
      }
  
      const PhasesLabel: any = {
        'pre-approval': 'Pre Approval',
        'approval': 'Approval Tasks',
        'due-diligence': 'Due Diligence',
        'final': 'Final Approval',
        'closing': 'Closing'
      }
      
      return {
        phaseLast: {
          key: phaseLastKey,
          label: PhasesLabel[phaseLastKey as any]
        },
        taskLast
      }
  }

  const redirectLoan = async (loan: Loan) => {
    if (useRedirectToDashboardMode) {
      await redirectToDashboard(loan._id);
    } else {
      navigate(`/home/${loan._id}`);
      navigate(0);
    }
    setHandlerVisible(false);
  }

  const displayedEntities = (isPaused || (fetching && page === 1)) ? [] : allLoadedEntities;

  return (
    <RightScenes
      visible={visible}
      handlerSetVisibile={setHandlerVisible}
    >
      <div className='view-entities'>
        <div className='view-entities-header'>
          <h1 className='title'>Entities</h1>
          <div className='search-box'>
            <img src={searchIcon} alt="search" />
            <input
              type="text"
              placeholder="Search by entity or guarantor name"
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
        </div>

        <div className='view-entities-content'>
          {displayedEntities.map((entity: any) => (
            <div key={entity._id} className='card-entity'>
              <div className='card-entity-header'>
                <h3>{entity.name}</h3>
                <div className='card-entity-header-description'>
                  <p>{countGuarantors(entity as any)} Guarantors | {entity.loans?.length} Loans</p>
                  <Dropdown
                    className="DrodownBlue see-loan"
                    menu={{
                      items: entity.loans.map((loan: Loan, index: number) => ({
                        key: index,
                        label: (
                          <div className='item-loan' onClick={() => redirectLoan(loan as any)}>
                            <div className='item-loan-state'>
                              <span className='tag-state-blue' style={{textTransform: 'uppercase'}}>
                                {getStateLoan(loan)}
                              </span>
                            </div>
                            <span className='item-loan-name'>
                              <b>{loan.name}</b> <span>| {loan.type}</span>
                            </span>
                            <span className='item-loan-view-loan'>View loan &#8594;</span>
                          </div>
                        ),
                      })), onClick: () => { }
                    }}
                    placement="bottomRight"
                    overlayClassName="DrodownBlue-blue entities-loans-dropdown"
                  >
                    <Button type="link" className="button button-link-blue">
                      See loans
                    </Button>
                  </Dropdown>
                </div>
              </div>
              <div className='card-entity-content'>
                <div className='card-entity-content-guarantors'>
                  <div className='card-entity-content-resume hover-edit'>
                    <label>Guarantors</label>
                    <span>{countGuarantors(entity as any, true)}/{countGuarantors(entity as any)} complete profiles</span>
                    <div className='box-shadow'>
                      <span>Click “See loans” and open an active loan to edit guarantor profiles</span>
                    </div>
                  </div>
                  <div className='guarantors-names'>
                    {getGuarantorsName(entity as any)}
                  </div>
                </div>
                <div className='card-entity-content-documents'>
                  <div className='card-entity-content-resume'>
                    <label>Documents</label>
                    <span>{countDocs(entity, true)}/{countDocs(entity)} unsubmitted docs</span>
                  </div>
                  <div className='doc-states'>
                    <div className={`circles-tags`}>
                      {entity.docs.map((doc: any, index: number) => (
                        <div key={index}>
                          <div
                            data-tooltip-id={`circle-state-${index}`}
                            data-tooltip-html={`<b>${TOOLTIP_STATES[doc.state]}</b>`}
                            className={`circle circle-${doc.state}`}
                          />
                          <Tooltip
                            id={`circle-state-${index}`}
                            className={`circle-state-tooltip circle-state-tooltip-${doc.state}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {hasMore && !fetching && <ScrollSentinel ref={sentinelRef} />}
        </div>
      </div>
    </RightScenes>
  );
};

export default Entities;
